import { Injectable } from '@angular/core';
import { AggregateRootOfPractitionerPractitionerRole } from '../../apiV2/models/aggregate-root-of-practitioner-practitioner-role';
import {find, forEach, filter} from 'lodash-es';
import { AggregateRootOfFacilityOrganizationAffiliation } from '../../apiV2/models/aggregate-root-of-facility-organization-affiliation';
import {Address, Facility, Practitioner, Specialty} from '../../apiV2/models';
import { getCrisisServicesFromRelation } from './behavioral-health.utils';

export interface SearchCardData {
  list: {
    //temp removing until we figure out tying min_distance with location
    Address: Address;
    Phone: string | undefined;
    Gender?: string;
    BoardCertified?: string | undefined;
    Fax: string | undefined;
    Email: string | undefined;
    Website: string | undefined;
  };
  extras: {
    RootStableId: string;
    Name: string | undefined;
    Alias?: string | undefined;
    Specialties: string[] | undefined;
    Qualification: string;
    MinDistance: string | undefined;
    StandardAddress: StandardAddress | undefined;
    Lat?: string | undefined;
    Lon?: string | undefined;
    Facility?: string | undefined;
    NPI?: string | undefined;
    Gender?: string | undefined;
    ProviderLanguages?: string[] | undefined;
    ProviderLanguagesString?: string | undefined;
    LocationStableIdHash?: string | undefined;
    behaviorHealth?: {
      communication: string[];
      affiliation: string[];
      ageLimit: string[];
      conditionsTreated: string[];
      levelOfCare: string[];
    };
    crisisServices?: {
      communication: string[];
      type: string[];
      telecom: {
        phone: string[];
        email: string[];
      };
      coverageArea: string[];
      availableTime: any[]; // keep exactly as provided (no transformation)
      ageLimit: string[];
    };
    crisis_services_health?: {
      communication: string[];
      type: string[];
      telecom: {
        phone: string[];
        email: string[];
      };
      coverageArea: string[];
      availableTime: any[];
      ageLimit: string[];
    };
    ReportIssueLocationsJson?: string | undefined;
  };
  identifiers: {
    Licenses: string[];
  };
}

export interface StandardAddress {
  LineOne: string,
  LineTwo: string
}

@Injectable({
  providedIn: 'root',
})
export class SearchResultCardAdapterV2Service {
  constructor() {}

  private getUniqueIdentifiersProvider(data: Practitioner | undefined): string[] {
    const res: string[] = [];
    if (data && data.identifier) {
      forEach(data.identifier, (c) => {
        if (c.type == 'NPI' || c.type == 'LN') {
          res.push(`${c.type}: ${c.value}`);
        }
      });
    }
    return res;
  }

  private getNpiValue(data: { identifier?: Array<{ type?: string; value?: string }> } | undefined): string | undefined {
    if (!data || !Array.isArray((data as any).identifier)) return undefined;
    const entry = (data as any).identifier.find((i: any) => i && i.type === 'NPI' && i.value);
    return entry?.value;
  }

  private getReportIssueLocationsJson(relations: any[] | undefined): string {
    if (!Array.isArray(relations)) return '[]';

    const seen = new Set<string>();
    const locations: Array<{ value: string; description: string }> = [];

    relations.forEach((relation: any) => {
      relation?.location?.forEach((location: any) => {
        location?.address?.forEach((address: any) => {
          const addressText = address?.text?.trim?.() || '';

          if (!addressText || seen.has(addressText)) return;

          seen.add(addressText);

          locations.push({
            value: addressText,
            description: addressText,
          });
        });
      });
    });

    return JSON.stringify(locations);
  }

  private getPrimarySpecialty(data: Array<Specialty> | null | undefined): string | undefined{
    if(data){
      const res = filter(data, ['primary' ,true]);
      const primaryName = res[0].display;
      return primaryName;
    }else{
      return 'N/A'
    }
  }

  /**
   * Services extraction rule:
   * 1. Prefer relation.services if present and non-empty
   * 2. Else use relation.location[0].services if present and non-empty
   * 3. Else fall back to relation.location[0] only for location-level fields such as levelOfCare
   */
  private getServiceLikeSourcesFromRelation(relation: any): { sources: any[]; fallbackLocation?: any } {
    const relationServices = relation?.services;
    const fallbackLocation = relation?.location?.[0];
    const locationServices = fallbackLocation?.services;

    if (Array.isArray(relationServices) && relationServices.length > 0) {
      return { sources: relationServices, fallbackLocation };
    }

    if (Array.isArray(locationServices) && locationServices.length > 0) {
      return { sources: locationServices, fallbackLocation };
    }

    if (fallbackLocation) {
      return { sources: [fallbackLocation], fallbackLocation };
    }

    return { sources: [], fallbackLocation: undefined };
  }

  private uniqStrings(values: Array<any>): string[] {
    const s = new Set<string>();
    values.forEach(v => {
      const str = (v ?? '').toString().trim();
      if (str) s.add(str);
    });
    return Array.from(s);
  }

  private extractDisplays(arr: any[] | null | undefined): string[] {
    if (!Array.isArray(arr)) return [];
    return this.uniqStrings(arr.map(x => x?.display));
  }

  private extractTexts(arr: any[] | null | undefined): string[] {
    if (!Array.isArray(arr)) return [];
    return this.uniqStrings(arr.map(x => x?.text));
  }

  private extractCoverageAreaDisplays(arr: any[] | null | undefined): string[] {
    if (!Array.isArray(arr)) return [];
    return this.uniqStrings(arr.map(x => x?.display));
  }

  private extractLevelOfCareFromLocationType(locationObj: any): string[] {
    const typeArr = locationObj?.type;
    if (!Array.isArray(typeArr)) return [];
    return this.uniqStrings(typeArr.map((t: any) => t?.text));
  }

  private ageGroups(): Array<{ label: string; low: number; high: number }> {
    return [
        { label: '(0-12)', low: 0, high: 12 },
      { label: '(13-17)', low: 13, high: 17 },
      { label: '(18-64)', low: 18, high: 64 },
      { label: '(65+)', low: 65, high: Number.POSITIVE_INFINITY },
    ];
  }

  private rangesOverlap(aLow: number, aHigh: number, bLow: number, bHigh: number): boolean {
    // Treat invalid highs as open-ended
    const ah = Number.isFinite(aHigh) ? aHigh : Number.POSITIVE_INFINITY;
    const bh = Number.isFinite(bHigh) ? bHigh : Number.POSITIVE_INFINITY;
    return aLow <= bh && bLow <= ah;
  }

  /**
   * ageLimit comes as range objects (often in services[].ageLimit[]):
   * { low: 0, high: 64 } -> determine overlapping predefined groups.
   */
  private mapAgeLimitRangesToGroups(ageLimitArr: any[] | null | undefined): string[] {
    if (!Array.isArray(ageLimitArr) || ageLimitArr.length === 0) return [];

    const out = new Set<string>();
    const groups = this.ageGroups();

    ageLimitArr.forEach(r => {
      const low = Number(r?.low);
      const highRaw = r?.high;
      const high = highRaw === undefined || highRaw === null ? Number.POSITIVE_INFINITY : Number(highRaw);

      if (!Number.isFinite(low)) return;

      groups.forEach(g => {
        if (this.rangesOverlap(low, high, g.low, g.high)) out.add(g.label);
      });
    });

    return Array.from(out);
  }

  private extractTelecomSplit(telecomArr: any[] | null | undefined): { phone: string[]; email: string[] } {
    const phone: string[] = [];
    const email: string[] = [];
    if (Array.isArray(telecomArr)) {
      telecomArr.forEach(t => {
        const system = (t?.system ?? '').toString().trim();
        const value = (t?.value ?? '').toString().trim();
        if (!value) return;
        if (system === 'phone') phone.push(value);
        if (system === 'email') email.push(value);
      });
    }
    return { phone: this.uniqStrings(phone), email: this.uniqStrings(email) };
  }

  /**
   * Implements:
   * 1) Behavior Health: merge ALL entries with category.text === "Behavior Health" into ONE consolidated object, dedup arrays.
   * 2) Crisis Services: resolved via getCrisisServicesFromRelation (DRY shared helper).
   *    Priority: relation.services[] where category.text === "Crisis Services"
   *    Fallback: relation.location.services where category.text === "Crisis Services"
   */
  private extractBehaviorHealthAndCrisisServicesFromRelation(relation: any): {
    behaviorHealth: {
      communication: string[];
      affiliation: string[];
      ageLimit: string[];
      conditionsTreated: string[];
      levelOfCare: string[];
    };
    crisisServices: {
      communication: string[];
      type: string[];
      telecom: { phone: string[]; email: string[] };
      coverageArea: string[];
      availableTime: any[];
      ageLimit: string[];
    };
  } {
    const { sources, fallbackLocation } = this.getServiceLikeSourcesFromRelation(relation);

    const behaviorHealthSets = {
      communication: new Set<string>(),
      affiliation: new Set<string>(),
      ageLimit: new Set<string>(),
      conditionsTreated: new Set<string>(),
      levelOfCare: new Set<string>(),
    };

    // Process Behavior Health entries only
    sources.forEach((src: any) => {
      const categoryText = (src?.category?.text ?? '').toString().trim();

      if (categoryText === 'Behavior Health') {
        this.extractDisplays(src?.communication).forEach(v => behaviorHealthSets.communication.add(v));
        this.extractTexts(src?.affiliation).forEach(v => behaviorHealthSets.affiliation.add(v));
        this.extractDisplays(src?.conditionsTreated).forEach(v => behaviorHealthSets.conditionsTreated.add(v));
        this.mapAgeLimitRangesToGroups(src?.ageLimit).forEach(v => behaviorHealthSets.ageLimit.add(v));
        // levelOfCare is explicitly from location.type (not from the service itself)
        this.extractLevelOfCareFromLocationType(fallbackLocation).forEach(v => behaviorHealthSets.levelOfCare.add(v));
      }
    });

    // Special fallback for levelOfCare if behaviorHealth is still empty but location exists
    if (behaviorHealthSets.communication.size === 0 &&
        behaviorHealthSets.affiliation.size === 0 &&
        behaviorHealthSets.conditionsTreated.size === 0 &&
        behaviorHealthSets.ageLimit.size === 0 &&
        fallbackLocation) {
      this.extractLevelOfCareFromLocationType(fallbackLocation).forEach(v => behaviorHealthSets.levelOfCare.add(v));
    }

    // Resolve Crisis Services using the shared DRY helper:
    // priority: relation.services[] → fallback: relation.location.services
    const crisisSets = {
      communication: new Set<string>(),
      type: new Set<string>(),
      phone: new Set<string>(),
      email: new Set<string>(),
      coverageArea: new Set<string>(),
      availableTime: [] as any[],
      ageLimit: new Set<string>(),
    };

    getCrisisServicesFromRelation(relation).forEach((src: any) => {
      this.extractDisplays(src?.communication).forEach(v => crisisSets.communication.add(v));
      this.extractTexts(src?.type).forEach(v => crisisSets.type.add(v));
      const telecom = this.extractTelecomSplit(src?.telecom);
      telecom.phone.forEach(v => crisisSets.phone.add(v));
      telecom.email.forEach(v => crisisSets.email.add(v));
      this.extractCoverageAreaDisplays(src?.coverageArea).forEach(v => crisisSets.coverageArea.add(v));
      if (Array.isArray(src?.availableTime)) {
        crisisSets.availableTime.push(...src.availableTime);
      }
      this.mapAgeLimitRangesToGroups(src?.ageLimit).forEach(v => crisisSets.ageLimit.add(v));
    });

    return {
      behaviorHealth: {
        communication: Array.from(behaviorHealthSets.communication),
        affiliation: Array.from(behaviorHealthSets.affiliation),
        ageLimit: Array.from(behaviorHealthSets.ageLimit),
        conditionsTreated: Array.from(behaviorHealthSets.conditionsTreated),
        levelOfCare: Array.from(behaviorHealthSets.levelOfCare),
      },
      crisisServices: {
        communication: Array.from(crisisSets.communication),
        type: Array.from(crisisSets.type),
        telecom: {
          phone: Array.from(crisisSets.phone),
          email: Array.from(crisisSets.email),
        },
        coverageArea: Array.from(crisisSets.coverageArea),
        availableTime: crisisSets.availableTime,
        ageLimit: Array.from(crisisSets.ageLimit),
      },
    };
  }

  adaptProvider(
    data: AggregateRootOfPractitionerPractitionerRole
  ): SearchCardData {
    const goldenRecord = find(data?.heads, { golden: true });

    let temp = data.relations![0];
    let tempAddress = temp.location![0].address![0];
    const regex = /(\d+\.\d{4})\d*/;

    let minDistance = String(data.meta!['min_distance']).replace(regex, '$1');
    // let certified = temp.specialty?[0].attrs.boardCertified;

    // Extract telecom information
    const telecomList = temp.location?.[0]?.telecom || [];
    const phone = telecomList.find(item => item.system === 'phone')?.value || '';
    const fax = telecomList.find(item => item.system === 'fax')?.value || '';
    // Prefer 'url' system, but fall back to 'website' if present in some datasets
    const website = (telecomList.find(item => item.system === 'url')?.value)
      || (telecomList.find(item => item.system === 'website')?.value)
      || '';
    const email = telecomList.find(item => item.system === 'email')?.value || '';

    // Extract languages from all heads[].communication[].display, deduped
    const heads: any[] = (data as any)?.heads || [];
    const langSet = new Set<string>();
    heads.forEach(h => {
      const comms = h?.communication;
      if (Array.isArray(comms)) {
        comms.forEach((c: any) => {
          const d = (c?.display ?? c?.language?.text ?? c?.value ?? '').toString().trim();
          if (d) langSet.add(d);
        });
      }
    });

    // Fallback: Extract languages from the selected location's communication array if heads yielded nothing
    if (langSet.size === 0) {
      const locationCommunications: any[] = (temp as any)?.location?.[0]?.communication || [];
      if (Array.isArray(locationCommunications)) {
        locationCommunications.forEach((c: any) => {
          const d = (c?.display ?? c?.language?.text ?? c?.value ?? c?.code ?? '').toString().trim();
          if (d) langSet.add(d);
        });
      }
    }

    const providerLanguages = Array.from(langSet);
    const providerLanguagesString = (providerLanguages.length ? providerLanguages : ['English']).join(', ');
    // Extract unique qualifications
    const qualifications = this.getUniqueQualifications(goldenRecord);

    const svc = this.extractBehaviorHealthAndCrisisServicesFromRelation(temp);

    let specialties = this.getSpecialties(temp.specialty);
    if (!specialties || specialties.length === 0) {
      specialties = this.getSpecialties(temp.location?.[0]?.specialty);
    }

    return {
      list: {
        Address: tempAddress,
        Phone: phone,
        Gender: goldenRecord?.gender?.value ? goldenRecord.gender.value.charAt(0).toUpperCase() + goldenRecord.gender.value.slice(1).toLowerCase() : undefined,
        // BoardCertified: certified,
        Fax: fax,
        Email: email,
        Website: website,
      },
      extras: {
        RootStableId: data.stableId!,
        Name: goldenRecord?.name![0].text!,
        Alias: (goldenRecord as any)?.heads?.[0]?.alias?.trim?.() || (goldenRecord as any)?.heads?.[0]?.alias,
        Specialties: specialties,
        Qualification: qualifications,
        MinDistance: minDistance,
        StandardAddress: this.buildStandardAddress(temp.location![0].address![0]),
        Lat: temp.location?.[0]?.position?.[0]?.lat !== undefined && temp.location?.[0]?.position?.[0]?.lat !== null
          ? String(temp.location?.[0].position?.[0].lat)
          : undefined,

        Lon: temp.location?.[0]?.position?.[0]?.lon !== undefined && temp.location?.[0]?.position?.[0]?.lon !== null
          ? String(temp.location?.[0].position?.[0].lon)
          : undefined,
        Facility: temp.location?.[0]?.facility?.[0]?.name?.value,
        NPI: this.getNpiValue(goldenRecord),
        Gender: goldenRecord?.gender?.value ? goldenRecord.gender.value.charAt(0).toUpperCase() + goldenRecord.gender.value.slice(1).toLowerCase() : undefined,
        ProviderLanguages: providerLanguages,
        ProviderLanguagesString: providerLanguagesString,
        LocationStableIdHash: temp.location?.[0]?.stableIdHash,
        behaviorHealth: svc.behaviorHealth,
        crisisServices: svc.crisisServices,
        crisis_services_health: svc.crisisServices,
        ReportIssueLocationsJson: this.getReportIssueLocationsJson(data?.relations as any[]),
      },
      identifiers: {
        Licenses: this.getUniqueIdentifiersProvider(goldenRecord),
      },
    };
  }

  private getUniqueQualifications(data: Practitioner | undefined): string {
    if (!data || !data.qualification || data.qualification.length === 0) {
      return ''; // Return empty string if no qualifications
    }

    // Extract unique qualification codes
    const uniqueQualificationCodes = new Set<string>();

    forEach(data.qualification, (qualification) => {
      if (qualification.code) {
        uniqueQualificationCodes.add(qualification.code);
      }
    });

    // Convert to array and join with commas
    const qualificationArray = Array.from(uniqueQualificationCodes);
    return qualificationArray.join(', ');
  }

  private getSpecialties(specialties: Array<Specialty> | null | undefined): string[] {
    if (!specialties || specialties.length === 0) {
      return [];
    }

    const specialtySet = new Set<string>();

    forEach(specialties, (specialty) => {
      if (specialty.display) {
        specialtySet.add(specialty.display);
      }
    });

    return Array.from(specialtySet);
  }

  private getUniqueIdentifiersFacility(data: Facility | undefined): string[] {
    let res = new Set<string>();
    if (data) {
      const facilityIdentifier = data.identifier;
      forEach(facilityIdentifier, (i) => {
        if (i.type != 'TAX') {
          res.add(`${i.type}: ${i.value}`);
        }
      });
    }

    return Array.from(res);
  }

  adaptFacility(
    data: AggregateRootOfFacilityOrganizationAffiliation
  ): SearchCardData {
    const goldenRecord = find(data?.heads, { golden: true });
    let temp = data.relations![0];
    let tempAddress = temp.location![0].address![0];
    const regex = /(\d+\.\d{4})\d*/;

    let minDistance = String(data.meta!['min_distance']).replace(regex, '$1');

    // Extract telecom information
    const telecomList = temp.location?.[0]?.telecom || [];
    const phone = telecomList.find(item => item.system === 'phone')?.value || '';
    const fax = telecomList.find(item => item.system === 'fax')?.value || '';
    // Prefer 'url' system, but fall back to 'website' if present in some datasets
    const website = (telecomList.find(item => item.system === 'url')?.value)
      || (telecomList.find(item => item.system === 'website')?.value)
      || '';
    const email = telecomList.find(item => item.system === 'email')?.value || '';

    // Extract languages from the selected location's communication array
    // Source: data.facilities[0].relations[0].location[0].communication[]
    const facilityLangSet = new Set<string>();
    const heads: any[] = (data as any)?.heads || [];
    heads.forEach(h => {
      const comms = h?.communication;
      if (Array.isArray(comms)) {
        comms.forEach((c: any) => {
          const d = (c?.display ?? c?.language?.text ?? c?.value ?? '').toString().trim();
          if (d) facilityLangSet.add(d);
        });
      }
    });

    // Fallback: Extract languages from location communication if heads yielded nothing
    if (facilityLangSet.size === 0) {
      const locationCommunications: any[] = (temp as any)?.location?.[0]?.communication || [];
      if (Array.isArray(locationCommunications)) {
        locationCommunications.forEach((c: any) => {
          const d = (c?.display ?? c?.language?.text ?? c?.value ?? c?.code ?? '').toString().trim();
          if (d) facilityLangSet.add(d);
        });
      }
    }
    const facilityLanguages = Array.from(facilityLangSet);
    const facilityLanguagesString = (facilityLanguages.length ? facilityLanguages : ['English']).join(', ');

    let specialties = temp.specialty;
    if (!specialties || specialties.length === 0) {
      specialties = temp.location?.[0]?.specialty;
    }

    const svc = this.extractBehaviorHealthAndCrisisServicesFromRelation(temp);

    return {
      list: {
        Address: tempAddress,
        Phone: phone,
        Fax: fax,
        Email: email,
        Website: website,
      },
      extras: {
        RootStableId: data.stableId!,
        Name: (goldenRecord as any)?.name?.[0]?.value,
        Alias: (goldenRecord as any)?.alias?.value,
        Specialties: this.getSpecialties(specialties),
        Qualification: '',
        MinDistance: minDistance,
        StandardAddress: this.buildStandardAddress(temp.location![0].address![0]),
        NPI: this.getNpiValue(goldenRecord),
        ProviderLanguages: facilityLanguages,
        ProviderLanguagesString: facilityLanguagesString,
        behaviorHealth: svc.behaviorHealth,
        crisisServices: svc.crisisServices,
        crisis_services_health: svc.crisisServices,
        ReportIssueLocationsJson: this.getReportIssueLocationsJson(data?.relations as any[]),
      },
      identifiers: {
        Licenses: this.getUniqueIdentifiersFacility(goldenRecord),
      },
    };
  }

  buildStandardAddress(address: Address): StandardAddress {
    let lineOne = '';
    let lineTwo = '';
    if(address.line) {
      lineOne = address.line[0]
      if(address.line.length > 1) {
        lineOne += ' ' + address.line[1]
      }
    }
    lineTwo = address.city + ', ' + address.state + ', ' + address.postalCode?.split('-')[0]

    return {LineOne: lineOne, LineTwo: lineTwo}
  }
}
